import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import HttpClient from '@/api/HttpClient';

const httpClient = new HttpClient();

export interface TomVendingVariantUpc {
  id: number;
  upc: string;
  label: string | null;
}

export interface TomVendingInventoryRow {
  id: number;
  upc: string;
  name: string | null;
  description: string | null;
  imageS3Key: string | null;
  thumbnailS3Key: string | null;
  imageUrl?: string | null;
  thumbnailUrl?: string | null;
  priceCents?: number | null;
  costCents?: number | null;
  brandId?: number | null;
  brandName?: string | null;
  flavor?: string | null;
  weightText?: string | null;
  categoryId?: number | null;
  categoryName?: string | null;
  schoolApproved?: boolean;
  internalNotes?: string | null;
  preferredVendorId?: number | null;
  preferredVendorName?: string | null;
  altIdentifier1?: string | null;
  altIdentifier2?: string | null;
  altIdentifier3?: string | null;
  altIdentifier4?: string | null;
  variantUpcs?: TomVendingVariantUpc[];
  createdAt?: string;
  updatedAt?: string;
}

export type TomVendingInventoryBatchUpdateRow = {
  id: number;
  priceCents?: number | null;
  costCents?: number | null;
  brandId?: number | null;
  flavor?: string | null;
  weightText?: string | null;
  categoryId?: number | null;
  schoolApproved?: boolean;
  internalNotes?: string | null;
  preferredVendorId?: number | null;
};

export type TvBrand = {
  id: number;
  name: string;
  active?: boolean;
};

export async function queryTomVendingBrands(
  search = '',
  options?: { includeInactive?: boolean },
): Promise<TvBrand[]> {
  const res = await httpClient.post('api/tom-vending/inventory/brand/query', {
    search,
    take: 500,
    includeInactive: options?.includeInactive ?? false,
  });
  return (res.data as { data?: TvBrand[] }).data ?? [];
}

export async function saveTomVendingBrand(brand: Partial<TvBrand> & { name: string }): Promise<TvBrand> {
  const res = await httpClient.post('api/tom-vending/inventory/brand/save', brand);
  return (res.data as { brand: TvBrand }).brand;
}

export const useTomVendingBrandsList = (search = '', includeInactive = false) => {
  return useQuery({
    queryKey: ['tom-vending-brands', search, includeInactive],
    queryFn: () => queryTomVendingBrands(search, { includeInactive }),
    staleTime: 60_000,
  });
};

export const useSaveTomVendingBrand = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveTomVendingBrand,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tom-vending-brands'] });
      queryClient.invalidateQueries({ queryKey: ['tom-vending-inventory'] });
    },
  });
};

export type TvCategory = {
  id: number;
  name: string;
  active?: boolean;
};

export async function queryTomVendingCategories(
  search = '',
  options?: { includeInactive?: boolean },
): Promise<TvCategory[]> {
  const res = await httpClient.post('api/tom-vending/inventory/category/query', {
    search,
    take: 500,
    includeInactive: options?.includeInactive ?? false,
  });
  return (res.data as { data?: TvCategory[] }).data ?? [];
}

export async function saveTomVendingCategory(category: Partial<TvCategory> & { name: string }): Promise<TvCategory> {
  const res = await httpClient.post('api/tom-vending/inventory/category/save', category);
  return (res.data as { category: TvCategory }).category;
}

export const useTomVendingCategoriesList = (search = '', includeInactive = false) => {
  return useQuery({
    queryKey: ['tom-vending-categories', search, includeInactive],
    queryFn: () => queryTomVendingCategories(search, { includeInactive }),
    staleTime: 60_000,
  });
};

export const useSaveTomVendingCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveTomVendingCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tom-vending-categories'] });
      queryClient.invalidateQueries({ queryKey: ['tom-vending-inventory'] });
    },
  });
};

export async function batchUpdateTomVendingInventory(params: {
  rows: TomVendingInventoryBatchUpdateRow[];
  monetaryChangeNote?: string | null;
}): Promise<{ updated: number; failed: Array<{ id: number; error: string }> }> {
  const response = await httpClient.post('api/tom-vending/inventory/batch-update', params);
  return response.data as { updated: number; failed: Array<{ id: number; error: string }> };
}

export interface InventoryQueryParams {
  skip: number;
  take: number;
  search: string;
  orderBy: string;
  orderDirection: 'ASC' | 'DESC';
  /** Narrow pending overlay to this open site visit */
  siteVisitId?: number;
  /** Pending overlay merges all open visits at this Tom’s vending location */
  effectiveOpenPendingAcrossLocation?: boolean;
  /** Skip per-row S3 presigning (faster bulk lists e.g. quick edit). Default true. */
  includeSignedUrls?: boolean;
  /** Skip loading alternate UPC variants. Default true. */
  includeVariants?: boolean;
}

export async function queryTomVendingInventory(params: InventoryQueryParams): Promise<{
  data: TomVendingInventoryRow[];
  total: number;
}> {
  const response = await httpClient.post('api/tom-vending/inventory/query', params);
  const d = response.data as { data?: TomVendingInventoryRow[]; total?: number };
  return { data: d.data || [], total: d.total ?? 0 };
}

export async function lookupInventoryUpc(upc: string): Promise<{
  found: boolean;
  source?: 'openfoodfacts' | 'upcitemdb' | 'go_upc_ai';
  code?: string;
  name?: string | null;
  description?: string | null;
  imageUrl?: string | null;
  imageThumbUrl?: string | null;
}> {
  const response = await httpClient.post('api/tom-vending/inventory/lookup-upc', { upc });
  return response.data as any;
}

export async function importOffInventoryImages(body: {
  imageUrl?: string;
  imageThumbUrl?: string;
}): Promise<{ imageS3Key: string | null; thumbnailS3Key: string | null }> {
  const response = await httpClient.post('api/tom-vending/inventory/import-images-from-off', body);
  return response.data as { imageS3Key: string | null; thumbnailS3Key: string | null };
}

export async function getInventoryImageUploadUrl(
  fileExtension: string,
  contentType: string,
): Promise<{ uploadUrl: string; s3Key: string }> {
  const response = await httpClient.get<{ uploadUrl: string; s3Key: string }>(
    'api/tom-vending/inventory/image-upload-url',
    { fileExtension, contentType } as Record<string, string>,
  );
  return response.data as unknown as { uploadUrl: string; s3Key: string };
}

export async function getInventoryThumbnailUploadUrl(
  fileExtension: string,
  contentType: string,
): Promise<{ uploadUrl: string; s3Key: string }> {
  const response = await httpClient.get<{ uploadUrl: string; s3Key: string }>(
    'api/tom-vending/inventory/thumbnail-upload-url',
    { fileExtension, contentType } as Record<string, string>,
  );
  return response.data as unknown as { uploadUrl: string; s3Key: string };
}

export async function uploadToPresignedUrl(uploadUrl: string, file: File, contentType: string): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });
  if (!res.ok) {
    throw new Error(`Upload failed (${res.status})`);
  }
}

export const useCreateTomVendingInventoryItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const response = await httpClient.post('api/tom-vending/inventory/create', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tom-vending-inventory'] });
    },
  });
};

export const useUpdateTomVendingInventoryItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const response = await httpClient.post('api/tom-vending/inventory/update', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tom-vending-inventory'] });
    },
  });
};

export const useDeleteTomVendingInventoryItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { id: number }) => {
      const response = await httpClient.post('api/tom-vending/inventory/delete', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tom-vending-inventory'] });
    },
  });
};

export async function mergeTomVendingInventoryItems(payload: {
  keepInventoryItemId: number;
  mergeInventoryItemId: number;
}): Promise<void> {
  await httpClient.post('api/tom-vending/inventory/merge', payload);
}

export async function addTomVendingInventoryVariant(payload: {
  inventoryItemId: number;
  upc: string;
  label?: string | null;
}): Promise<{ id: number; upc: string; label: string | null }> {
  const response = await httpClient.post('api/tom-vending/inventory/variant/add', payload);
  const d = response.data as { variant?: { id: number; upc: string; label: string | null } };
  if (!d.variant) throw new Error('Bad response');
  return d.variant;
}

export async function removeTomVendingInventoryVariant(id: number): Promise<void> {
  await httpClient.post('api/tom-vending/inventory/variant/remove', { id });
}

export type AssociateAltIdentifierResult = {
  status: 'associated' | 'already_present' | 'slots_full' | 'conflict';
  field?: string;
  value?: string;
  otherItemId?: number;
};

/** Learn an invoice manufacturer/item # onto the next empty alt_identifier slot. */
export async function associateTomVendingAltIdentifier(payload: {
  inventoryItemId: number;
  identifier: string;
}): Promise<AssociateAltIdentifierResult> {
  const response = await httpClient.post('api/tom-vending/inventory/associate-alt-identifier', payload);
  return response.data as AssociateAltIdentifierResult;
}

export interface MonetaryHistoryRow {
  id: number;
  createdAt: string;
  field: string;
  previousValueCents: number | null;
  newValueCents: number | null;
  changedByUserId: number | null;
  changedByEmail: string | null;
  changedByDisplayName: string | null;
  note: string | null;
}

export async function queryTomVendingInventoryMonetaryHistory(params: {
  inventoryItemId: number;
  field?: 'price' | 'cost';
  skip?: number;
  take?: number;
}): Promise<{ data: MonetaryHistoryRow[]; total: number }> {
  const response = await httpClient.post('api/tom-vending/inventory/monetary-history/query', params);
  const d = response.data as { data?: MonetaryHistoryRow[]; total?: number };
  return { data: d.data || [], total: d.total ?? 0 };
}

export async function resolveTomVendingInventoryUpc(
  upc: string,
  opts?: { autoCreateInCatalog?: boolean },
): Promise<{
  found: boolean;
  inventoryItemId?: number;
  primaryUpc?: string;
  name?: string | null;
  imageUrl?: string | null;
  thumbnailUrl?: string | null;
}> {
  const response = await httpClient.post('api/tom-vending/inventory/resolve-upc-item', {
    upc,
    ...(opts?.autoCreateInCatalog ? { autoCreateInCatalog: true } : {}),
  });
  return response.data as {
    found: boolean;
    inventoryItemId?: number;
    primaryUpc?: string;
    name?: string | null;
    imageUrl?: string | null;
    thumbnailUrl?: string | null;
  };
}

export interface LocationCatalogRow extends TomVendingInventoryRow {
  totalAtLocation: number;
}

export interface MachineBreakdownRow {
  vendingMachineId: number;
  assetId: string;
  title: string | null;
  storageSiteId: number | null;
  quantity: number;
}

export interface MachineBalanceApiRow {
  balanceId: number;
  ledgerQuantity?: number;
  quantity: number;
  pending?: { parQty: number; fillQty: number; rejectQty: number; recycleQty: number } | null;
  inventoryItem: TomVendingInventoryRow;
}

export async function getInventoryWarehouseSite(): Promise<{ id: number; kind: string; label: string | null } | null> {
  const response = await httpClient.get('api/tom-vending/inventory/warehouse-site', {} as Record<string, string>);
  const d = response.data as { storageSite?: { id: number; kind: string; label: string | null } | null };
  return d.storageSite ?? null;
}

export async function upsertInventoryBalance(payload: {
  inventoryItemId: number;
  storageSiteId: number;
  quantity: number;
}): Promise<void> {
  await httpClient.post('api/tom-vending/inventory/balance/upsert', payload);
}

export async function upsertInventoryBalanceByMachine(payload: {
  vendingMachineId: number;
  inventoryItemId: number;
  quantity: number;
  locationId?: number;
}): Promise<void> {
  await httpClient.post('api/tom-vending/inventory/balance/upsert-by-machine', payload);
}

export async function queryMachineInventoryBalances(
  params: InventoryQueryParams & {
    vendingMachineId: number;
    locationId?: number;
    hideZeroQuantity?: boolean;
  },
): Promise<{ data: MachineBalanceApiRow[]; total: number }> {
  const { hideZeroQuantity, ...rest } = params;
  const response = await httpClient.post('api/tom-vending/inventory/balance/by-machine', {
    ...rest,
    ...(hideZeroQuantity ? { hideZeroQuantity: true } : {}),
  });
  const d = response.data as { data?: MachineBalanceApiRow[]; total?: number };
  return { data: d.data || [], total: d.total ?? 0 };
}

export type SiteVisitApplyLine = {
  inventoryItemId: number;
  recycleQty: number;
  rejectQty: number;
  parQty: number;
  fillQty: number;
};

export async function applySiteVisitInventory(payload: {
  siteVisitId: number;
  vendingMachineId: number;
  locationId: number;
  lines: SiteVisitApplyLine[];
}): Promise<{ success: boolean }> {
  const response = await httpClient.post('api/tom-vending/inventory/balance/site-visit-apply', payload);
  return response.data as { success: boolean };
}

export async function removeSiteVisitInventoryLine(payload: {
  siteVisitId: number;
  vendingMachineId: number;
  locationId: number;
  inventoryItemId: number;
}): Promise<{ success: boolean }> {
  const response = await httpClient.post('api/tom-vending/inventory/balance/site-visit-remove-line', payload);
  return response.data as { success: boolean };
}

export type LocationAdjustmentTargetMachine = {
  vendingMachineId: number;
  storageSiteId: number;
  assetId: string;
  title: string | null;
};

export type LocationAdjustmentWarehouseTarget = {
  storageSiteId: number;
  label: string | null;
  lastAuditAt?: string | null;
  lastAuditByDisplayName?: string | null;
};

export async function fetchLocationAdjustmentTargets(locationId: number): Promise<{
  warehouse: LocationAdjustmentWarehouseTarget | null;
  machines: LocationAdjustmentTargetMachine[];
}> {
  const response = await httpClient.post('api/tom-vending/inventory/balance/location-adjustment-targets', {
    locationId,
  });
  return response.data as {
    warehouse: LocationAdjustmentWarehouseTarget | null;
    machines: LocationAdjustmentTargetMachine[];
  };
}

export async function queryStorageSiteBalances(
  params: InventoryQueryParams & {
    storageSiteId: number;
    locationId: number;
    hideZeroQuantity?: boolean;
  },
): Promise<{ data: MachineBalanceApiRow[]; total: number }> {
  const { hideZeroQuantity, ...rest } = params;
  const response = await httpClient.post('api/tom-vending/inventory/balance/by-storage-site', {
    ...rest,
    ...(hideZeroQuantity ? { hideZeroQuantity: true } : {}),
  });
  const d = response.data as { data?: MachineBalanceApiRow[]; total?: number };
  return { data: d.data || [], total: d.total ?? 0 };
}

export type LocationAdjustmentLine = {
  storageSiteId: number;
  inventoryItemId: number;
  quantity: number;
};

export async function applyLocationInventoryAdjustment(payload: {
  locationId: number;
  lines: LocationAdjustmentLine[];
}): Promise<{ success: boolean }> {
  const response = await httpClient.post('api/tom-vending/inventory/balance/location-adjustment', payload);
  return response.data as { success: boolean };
}

export async function syncTomVendingSiteVisit(payload: {
  locationId: number;
  since: string;
  vendingMachineId?: number;
}): Promise<{
  lines: Array<{
    vendingMachineId: number;
    inventoryItemId: number;
    parQty: number;
    fillQty: number;
    rejectQty: number;
    recycleQty: number;
    updatedAt: string;
  }>;
}> {
  const response = await httpClient.post('api/tom-vending/inventory/balance/site-visit-sync', payload);
  return response.data as {
    lines: Array<{
      vendingMachineId: number;
      inventoryItemId: number;
      parQty: number;
      fillQty: number;
      rejectQty: number;
      recycleQty: number;
      updatedAt: string;
    }>;
  };
}

export async function syncTomVendingAdjustment(payload: {
  locationId: number;
  since: string;
  storageSiteId?: number;
}): Promise<{
  lines: Array<{ storageSiteId: number; inventoryItemId: number; quantity: number; updatedAt: string }>;
}> {
  const response = await httpClient.post('api/tom-vending/inventory/balance/adjustment-sync', payload);
  return response.data as {
    lines: Array<{ storageSiteId: number; inventoryItemId: number; quantity: number; updatedAt: string }>;
  };
}

export async function queryLocationInventoryCatalog(
  params: InventoryQueryParams & { locationId: number },
): Promise<{ data: LocationCatalogRow[]; total: number }> {
  const response = await httpClient.post('api/tom-vending/inventory/balance/location-catalog', params);
  const d = response.data as { data?: LocationCatalogRow[]; total?: number };
  return { data: d.data || [], total: d.total ?? 0 };
}

export async function queryLocationInventoryBreakdown(
  locationId: number,
  inventoryItemId: number,
): Promise<MachineBreakdownRow[]> {
  const response = await httpClient.post('api/tom-vending/inventory/balance/location-breakdown', {
    locationId,
    inventoryItemId,
  });
  const d = response.data as { machines?: MachineBreakdownRow[] };
  return d.machines || [];
}

export async function getInventoryBalanceQuantity(
  inventoryItemId: number,
  storageSiteId: number,
): Promise<number> {
  const response = await httpClient.post('api/tom-vending/inventory/balance/get', {
    inventoryItemId,
    storageSiteId,
  });
  const d = response.data as { quantity?: number };
  return d.quantity ?? 0;
}

export interface InventoryMovementRow {
  id: number;
  createdAt: string;
  userId: number | null;
  userEmail: string | null;
  userDisplayName: string | null;
  inventoryItemId: number;
  upc: string;
  itemName: string | null;
  quantity: number;
  /** adjustment | par | fill | recycle | reject — site visit / balance tooling */
  reason?: string | null;
  fromSiteId: number | null;
  fromLabel: string | null;
  fromKind: string | null;
  fromVendingMachineId: number | null;
  fromLocationId: number | null;
  toSiteId: number | null;
  toLabel: string | null;
  toKind: string | null;
  toVendingMachineId: number | null;
  toLocationId: number | null;
}

export async function queryInventoryMovements(params: {
  vendingMachineId?: number;
  locationId?: number;
  inventoryItemId?: number;
  reason?: string;
  skip: number;
  take: number;
}): Promise<{ data: InventoryMovementRow[]; total: number }> {
  const response = await httpClient.post('api/tom-vending/inventory/movement/query', params);
  const d = response.data as { data?: InventoryMovementRow[]; total?: number };
  return { data: d.data || [], total: d.total ?? 0 };
}

export interface TomVendingRejectSummaryRow {
  inventoryItemId: number;
  upc: string;
  itemName: string | null;
  fromLocationId: number | null;
  fromVendingMachineId: number | null;
  totalQty: number;
}

export async function queryTomVendingRejectSummary(params: {
  dateFrom: string;
  dateTo: string;
  locationId?: number;
  inventoryItemId?: number;
}): Promise<{ rows: TomVendingRejectSummaryRow[]; totalRejectedUnits: number }> {
  const response = await httpClient.post('api/tom-vending/inventory/movement/reject-summary', params);
  const d = response.data as { rows?: TomVendingRejectSummaryRow[]; totalRejectedUnits?: number };
  return { rows: d.rows || [], totalRejectedUnits: d.totalRejectedUnits ?? 0 };
}
